<template>
  <b-container fluid>
    <b-breadcrumb :items="breadcrumbs"/>
    <b-row>
      <b-col class="mb-3" lg="6">
        <b-card title="Welcome to My Vos Voco">
          <p class="card-text">
            Here are some common tasks you may wish to perform:
          </p>
          <b-list-group>
            <b-list-group-item v-for="task in tasks" :to="task.route" :key="task.route" class="align-items-start">
              <list-item :icon="task.icon" :heading="task.heading" :text="task.text" :disclosure="true"></list-item>
            </b-list-group-item>
          </b-list-group>
          <h4 class="mt-3" v-if="reports.length > 0">Reports</h4>
          <b-list-group v-if="reports.length > 0">
            <b-list-group-item v-for="(report, reportIndex) in reports" :to="reportLink(report)" :key="reportIndex" class="align-items-start">
              <list-item icon="report" :heading="report.name" :text="reportText(report)" :disclosure="true"></list-item>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>
      <b-col class="mb-3" lg="6" v-if="canRunReports">
        <b-card no-body class="pb-3 pl-1 pr-1" v-if="reportResults">
          <stacked-bar-chart :report="report" :results="reportResults" legendPosition="right" element-id="summary"></stacked-bar-chart>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-card no-body header="Recent Events">
          <b-list-group flush>
            <b-list-group-item v-for="event in events" :to="eventLink(event)" :key="event.id" class="align-items-start">
              <event-card :event="event"></event-card>
            </b-list-group-item>
          </b-list-group>
          <b-card-body>
            <p class="mt-3 mb-0 text-right">
              <b-button to="/events">See All Events</b-button>
            </p>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import _ from 'lodash'
import API from '@/lib/api'
import Permissions from '@/lib/permissions'
import Icon from '@/components/Icon'
import EventCard from '@/components/EventCard'
import StackedBarChart from '@/components/StackedBarChart'
import ListItem from '@/components/ListItem'

export default {
  name: 'Dashboard',
  components: { Icon, EventCard, StackedBarChart, ListItem },
  data () {
    return {
      breadcrumbs: [{text: 'My Vos Voco', to: '/'}, {text: 'Home', to: '/', active: true}],
      events: [],
      report: {
        name: 'Recent Events by Workflow',
        type: 'stacked',
        filters: [
        { type: 'logged_at', range_type: 'recent', period_type: 'days', count: 14, locked: true }
        ],
        group: [
          { column_id: 'date', sub_columns: [ { column_id: 'logged_at' } ], label: 'Date' },
          { column_id: 'workflow', label: 'Workflow' }
        ],
        chart_select: { column_id: 'count_distinct_events', label: 'Number of events' },
        settings: {
          order: [
            { column_id: 'date', sub_columns: [ { column_id: 'logged_at' } ], label: 'Date', direction: 'ascending' },
            { column_id: 'workflow', label: 'Workflow', direction: 'ascending' }
          ]
        }
      },
      reportResults: null,
      reports: []
    }
  },
  created () {
    this.fetchEvents()
    this.fetchReports()
    this.runReport()
  },
  watch: {
    workflow () {
      if (this.workflow != null) {
        this.chartFilters = { workflow_id: this.workflow.id, date_range: { type: 'all' } }
      } else {
        this.chartFilters = null
      }
    },
    workflows () {
      if (this.workflows.length > 0) {
        this.workflow = this.workflows[0]
      } else {
        this.workflow = null
      }
    }
  },
  computed: {
    workflow_options () {
      return this.workflows.map((workflow) => { return { text: workflow.name, value: workflow } })
    },
    canRunReports () {
      return Permissions.canRunReports(this.$store.state.user)
    },
    canPrint () {
      return Permissions.canPrint(this.$store.state.user)
    },
    tasks () {
      var tasks = [{
        route: '/events',
        icon: 'messages',
        heading: 'Messages & Cases',
        text: 'Manage received messages and case history'
      }]
      if (this.canPrint) {
        tasks.push({
          route: '/print',
          icon: 'print',
          heading: 'Print QR Codes',
          text: 'Print QR code label sheets for ad-hoc job tracking'
        })
      }
      tasks.push({
        route: '/settings',
        icon: 'settings',
        heading: 'Settings',
        text: 'Manage my account and login settings'
      })
      return tasks
    }
  },
  methods: {
    fetchEvents () {
      API.get('events', {context: 'all', page_size: 5}, this.$store.getters.authHeaders)
        .then((response) => {
          this.events = response.data.events
        })
    },
    fetchReports () {
      API.get('reports', {}, this.$store.getters.authHeaders)
        .then((response) => {
          this.reports = response.data.reports
        })
    },
    runReport () {
      API.post('/reports/run', {report: this.report}, this.$store.getters.authHeaders)
        .then((response) => {
          if (response.data.rows.length > 0) {
            this.reportResults = response.data
          }
        })
    },
    eventLink (event) {
      var teamId = null
      event.teams.forEach((team) => {
        if (teamId == null) {
          teamId = team.id
        }
        if (team.action_required) {
          teamId = team.id
        }
      })
      return {path: '/events/' + event.id, query: {team_id: teamId, context: 'events', return_to: '/events/'}}
    },
    reportText (report) {
      const typeName = {
        table: 'Table',
        donut: 'Donut Chart',
        line: 'Line Chart',
        bar: 'Bar Chart',
        stacked: 'Stacked Bar Chart'
      }[report.type]
      return `${typeName} Report`
    },
    reportLink (report) {
      var params = {report_id: report.id, page: 1, report: _.cloneDeep(report.report)}
      return {path: '/reports/run', query: params}
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/pages/Dashboard.vue